if (!customElements.get('ck-stories-editorial')) {
  class ClassickStoriesEditorial extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.links = Array.from(this.querySelectorAll('[data-chapter-link]'));
      this.chapters = this.links.map((link) => this.querySelector(link.hash)).filter(Boolean);
      this.progress = this.querySelector('[data-reading-progress]');
      this.animations = new Set();
      this.requestUpdate = () => {
        if (!this.frame) this.frame = requestAnimationFrame(() => { this.frame = 0; this.updateReadingPosition(); });
      };
      window.addEventListener('scroll', this.requestUpdate, { passive: true, signal: this.controller.signal });
      window.addEventListener('resize', this.requestUpdate, { passive: true, signal: this.controller.signal });
      this.motion.addEventListener('change', () => this.setupReveals(), { signal: this.controller.signal });
      this.addEventListener('click', (event) => {
        const link = event.target.closest('[data-chapter-link], [data-story-start]');
        if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const chapter = this.querySelector(link.hash);
        if (!chapter) return;
        event.preventDefault();
        chapter.scrollIntoView({ behavior: this.motion.matches ? 'instant' : 'smooth', block: 'start' });
        chapter.focus({ preventScroll: true });
      }, { signal: this.controller.signal });
      this.setupReveals();
      this.requestUpdate();
    }

    updateReadingPosition() {
      const nav = this.querySelector('.ck-journal__nav-shell');
      const threshold = nav ? nav.getBoundingClientRect().bottom + 50 : 160;
      let current = null;
      for (const chapter of this.chapters) {
        if (chapter.getBoundingClientRect().top <= threshold) current = chapter.id;
      }
      for (const link of this.links) {
        if (link.hash.slice(1) === current) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      }
      const rect = this.getBoundingClientRect();
      const distance = Math.max(1, this.offsetHeight - window.innerHeight);
      const value = Math.max(0, Math.min(1, -rect.top / distance));
      if (this.progress) this.progress.style.transform = `scaleX(${value})`;
    }

    setupReveals() {
      this.observer?.disconnect();
      for (const animation of this.animations) animation.cancel();
      this.animations.clear();
      if (this.motion.matches || !('IntersectionObserver' in window) || !Element.prototype.animate) return;
      this.observer = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          this.observer.unobserve(entry.target);
          if (entry.target.dataset.journalRevealed) continue;
          entry.target.dataset.journalRevealed = 'true';
          const isPhoto = entry.target.matches('figure');
          const animation = entry.target.animate(
            isPhoto
              ? [{ opacity: .72, transform: 'translateY(26px)' }, { opacity: 1, transform: 'translateY(0)' }]
              : [{ opacity: .55, transform: 'translateY(18px)' }, { opacity: 1, transform: 'translateY(0)' }],
            { duration: isPhoto ? 850 : 650, easing: 'cubic-bezier(.2,.65,.2,1)' }
          );
          this.animations.add(animation);
          animation.finished.then(() => this.animations.delete(animation), () => this.animations.delete(animation));
        }
      }, { threshold: .08, rootMargin: '0px 0px -20px 0px' });
      for (const target of this.querySelectorAll('[data-journal-reveal]')) {
        if (!target.dataset.journalRevealed) this.observer.observe(target);
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
      this.frame = 0;
      for (const animation of this.animations || []) animation.cancel();
      this.animations?.clear();
    }
  }
  customElements.define('ck-stories-editorial', ClassickStoriesEditorial);
}
